import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, BookX, Bookmark, BookmarkCheck, AlertTriangle, ListTree } from "lucide-react";
import { toast } from "sonner";
import NarrationBar from "@/components/reader/NarrationBar";
import ChapterDrawer, { currentChapterIndex } from "@/components/reader/ChapterDrawer";
import HighlightedText from "@/components/reader/HighlightedText";
import HighlightToolbar from "@/components/reader/HighlightToolbar";
import useReadingStats from "@/hooks/useReadingStats";
import FinishedDialog from "@/components/reader/FinishedDialog";
import { openBook, fetchPage, saveProgress, setBookmark, addHighlight, removeHighlight, daysUntil, errorDetail } from "@/lib/reader";

const NOTICES = {
  signed_out_elsewhere: "You were signed out because your account was signed in on another device.",
  402: "Your E-Reader pass has expired. Renew for another 40 days to keep reading.",
};

function ClosedPanel({ onReopen }) {
  return (
    <div className="max-w-xl mx-auto text-center py-24" data-testid="reader-book-closed">
      <BookX className="w-10 h-10 text-primary-gold mx-auto" />
      <h2 className="font-serif-display text-3xl mt-6">This book was closed</h2>
      <p className="text-soft mt-3">Another book was opened on your account. Only one book can be open at a time.</p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <button onClick={onReopen} data-testid="reader-reopen-button" className="bg-primary-gold text-[#0B0C10] font-mono-ui text-xs uppercase tracking-[0.2em] px-8 py-4">
          Reopen this book
        </button>
        <Link to="/reader" data-testid="reader-closed-library-link" className="btn-ghost font-mono-ui text-xs uppercase tracking-[0.2em] px-8 py-4">
          Back to library
        </Link>
      </div>
    </div>
  );
}

export default function ReaderBook() {
  const { bookId } = useParams();
  const location = useLocation();
  const [backTo] = useState(location.state?.from || null);
  const [listening, setListening] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [searchParams] = useSearchParams();
  const startPage = useRef(Number(searchParams.get("page")) || null).current;
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  useReadingStats(book?.id, listening);
  const [page, setPage] = useState(null);
  const [voice, setVoice] = useState("female");
  const [content, setContent] = useState(null);
  const [closed, setClosed] = useState(false);
  const [jump, setJump] = useState("");
  const [bookmarks, setBookmarks] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [showChapters, setShowChapters] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const articleRef = useRef(null);

  const handleError = useCallback(
    (e) => {
      const status = e?.response?.status;
      if (status === 409) return setClosed(true);
      if (status === 401 || status === 402) {
        const detail = e.response.data?.detail;
        const notice = NOTICES[detail] || NOTICES[status] || errorDetail(e);
        return navigate("/reader", { replace: true, state: { notice } });
      }
      setContent({ error: errorDetail(e) });
    },
    [navigate]
  );

  const open = useCallback(async () => {
    try {
      const res = await openBook(bookId);
      setBook(res.book);
      setVoice(res.voice || "female");
      setPage(startPage && startPage <= res.book.page_count ? startPage : res.page || 1);
      setBookmarks(res.bookmarks || []);
      setHighlights(res.highlights || []);
      setChapters(res.chapters || []);
      setExpiresAt(res.pass_expires_at);
      setClosed(false);
      if (startPage) window.history.replaceState(window.history.state, "", window.location.pathname);
    } catch (e) {
      handleError(e);
    }
  }, [bookId, startPage, handleError]);

  useEffect(() => {
    open();
  }, [open]);

  useEffect(() => {
    if (!book || !page) return;
    let cancelled = false;
    setContent(null);
    setRemoveTarget(null);
    fetchPage(book.id, page)
      .then((d) => !cancelled && setContent(d))
      .catch((e) => !cancelled && handleError(e));
    saveProgress({ book_id: book.id, page, voice })
      .then((r) => r.data?.finished_now && setCelebrate(r.data.completed_all ? "all" : "book"))
      .catch(() => {});
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      cancelled = true;
    };
  }, [book, page, voice, handleError]);

  const goTo = (n) => book && setPage(Math.min(Math.max(1, n), book.page_count));
  const isMarked = bookmarks.some((b) => b.page === page);
  const chapter = chapters[currentChapterIndex(chapters, page)];
  const pageHighlights = highlights.filter((h) => h.page === page);

  const handleAddHighlight = async (text, para, start) => {
    try {
      setHighlights(await addHighlight(book.id, page, text, para, start));
      toast.success("Highlighted — find it in Your Highlights.");
    } catch (e) {
      if (e?.response?.status === 400) toast.error(errorDetail(e));
      else handleError(e);
    }
  };

  const handleRemoveHighlight = async (id) => {
    try {
      setHighlights(await removeHighlight(book.id, id));
      setRemoveTarget(null);
      toast.success("Highlight removed.");
    } catch (e) {
      handleError(e);
    }
  };
  const daysLeft = expiresAt ? daysUntil(expiresAt) : null;

  const toggleBookmark = async () => {
    try {
      setBookmarks(await setBookmark(book.id, page, !isMarked));
      toast.success(isMarked ? `Bookmark removed from page ${page}.` : `Page ${page} bookmarked.`);
    } catch (e) {
      handleError(e);
    }
  };

  if (closed) return <ClosedPanel onReopen={open} />;

  return (
    <div className="min-h-screen pb-40" data-testid="reader-book-page">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0C10]/85 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to={backTo || "/reader"} data-testid="reader-back-link" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold shrink-0">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{backTo ? "Review books" : "Library"}</span>
          </Link>
          <div className="min-w-0 text-center">
            <p className="font-serif-display text-lg sm:text-xl truncate leading-tight" data-testid="reader-book-title">{book?.title}</p>
            {chapter && (
              <p className="hidden sm:block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-soft truncate" data-testid="reader-current-chapter">
                {chapter.title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <button
              onClick={() => setShowChapters(true)}
              disabled={!book}
              data-testid="reader-chapters-button"
              aria-label="Chapters"
              className="text-soft hover:text-primary-gold transition-colors"
            >
              <ListTree className="w-5 h-5" />
            </button>
            <button
              onClick={toggleBookmark}
              disabled={!book || !page}
              data-testid="reader-bookmark-toggle"
              aria-label={isMarked ? "Remove bookmark" : "Bookmark this page"}
              className={`transition-colors ${isMarked ? "text-primary-gold" : "text-soft hover:text-primary-gold"}`}
            >
              {isMarked ? <BookmarkCheck className="w-5 h-5 fill-current" /> : <Bookmark className="w-5 h-5" />}
            </button>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              goTo(Number(jump));
              setJump("");
            }}
            className="flex items-center gap-2 font-mono-ui text-xs text-soft shrink-0"
          >
            <input
              value={jump}
              onChange={(e) => setJump(e.target.value.replace(/\D/g, ""))}
              placeholder={page ? String(page) : "–"}
              className="w-12 bg-transparent border border-white/15 px-2 py-1 text-center text-text-primary focus:outline-none focus:border-primary-gold"
              aria-label="Go to page"
              data-testid="reader-page-jump"
            />
            <span data-testid="reader-page-total">/ {book?.page_count ?? "–"}</span>
          </form>
          </div>
        </div>
        {daysLeft !== null && daysLeft <= 3 && (
          <div className="border-t border-[#e0982f]/30 bg-[#e0982f]/10" data-testid="reader-expiry-strip">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-xs">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-[#e0982f] shrink-0" />
                Your pass ends {daysLeft === 0 ? "today" : daysLeft === 1 ? "tomorrow" : `in ${daysLeft} days`}.
              </span>
              <Link to="/reader" state={{ renew: true }} data-testid="reader-expiry-renew-link" className="font-mono-ui uppercase tracking-[0.18em] text-primary-gold hover:text-text-primary shrink-0">
                Renew →
              </Link>
            </div>
          </div>
        )}
      </header>

      <article className="max-w-2xl mx-auto px-6 pt-14" data-testid="reader-page-content">
        {!content ? (
          <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>
        ) : content.error ? (
          <p className="text-center text-soft py-32">{content.error}</p>
        ) : (
          <div ref={articleRef}>
            <HighlightedText
              text={content.text}
              highlights={pageHighlights}
              onMarkClick={(id, rect) => setRemoveTarget({ id, x: rect.left + rect.width / 2, y: rect.bottom })}
            />
          </div>
        )}

        {book && (
          <div className="flex items-center justify-between mt-16 border-t border-white/10 pt-8">
            <button onClick={() => goTo(page - 1)} disabled={page <= 1} data-testid="reader-prev-page" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span className="font-mono-ui text-xs text-soft" data-testid="reader-page-indicator">Page {page} of {book.page_count}</span>
            <button onClick={() => goTo(page + 1)} disabled={page >= book.page_count} data-testid="reader-next-page" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold disabled:opacity-30">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </article>

      {showChapters && (
        <ChapterDrawer
          chapters={chapters}
          page={page}
          onClose={() => setShowChapters(false)}
          onPick={(p) => {
            goTo(p);
            setShowChapters(false);
          }}
        />
      )}
      <HighlightToolbar
        containerRef={articleRef}
        onAdd={handleAddHighlight}
        removeTarget={removeTarget}
        onRemove={handleRemoveHighlight}
        onDismiss={() => setRemoveTarget(null)}
      />
      {book && page && (
        <NarrationBar
          onPlayingChange={setListening}
          chapters={chapters}
          bookId={book.id}
          page={page}
          total={book.page_count}
          voice={voice}
          onVoice={setVoice}
          onPageChange={goTo}
          onAuthError={handleError}
        />
      )}
      {celebrate && book && (
        <FinishedDialog productId={book.product_id} completionist={celebrate === "all"} onClose={() => setCelebrate(celebrate === "all" ? "book" : false)} />
      )}
    </div>
  );
}
